import { useState } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import {
    TestingActivityCurrentStep,
    TActsInProgActions,
    TActTableRow,
} from "api/generated/model";

interface UseReassignToolUtilsProps {
    actions: TActsInProgActions;
    selectedRows: Row<TActTableRow>[];
}

export function useReassignUtils({
    actions,
    selectedRows,
}: UseReassignToolUtilsProps) {
    const { t } = useTranslation();
    const [reassignModalOpened, setReassignModalOpened] = useState(false);
    const { textToolState: reassignToolState, maybeCurrentStep } =
        computeReassignToolState(t, actions, selectedRows);

    return {
        reassignModalOpened,
        setReassignModalOpened,
        reassignToolState,
        maybeCurrentStep,
    };
}
function computeReassignToolState(
    t: TFunction,
    actions: TActsInProgActions,
    selection: Row<TActTableRow>[]
): {
    textToolState: TextToolState;
    maybeCurrentStep: TestingActivityCurrentStep | null;
} {
    // Nothing selected
    if (selection.length === 0) {
        return { textToolState: { type: "DISABLED" }, maybeCurrentStep: null };
    }
    // Can not reassign archived activities
    if (selection.some((row) => row.original.archived)) {
        return {
            textToolState: {
                type: "DISABLED",
                reason: t("TestingActivities_Reassign_ArchivedActivities"),
            },
            maybeCurrentStep: null,
        };
    }
    // Insufficient access rights
    if (!actions.reassign) {
        return {
            textToolState: {
                type: "DISABLED",
                reason: t(
                    "TestingActivities_Reassign_InsufficientAccessRights"
                ),
            },
            maybeCurrentStep: null,
        };
    }
    // Can not reassign completed activities
    if (selection.some((row) => row.original.status === "completed")) {
        return {
            textToolState: {
                type: "DISABLED",
                reason: t("TestingActivities_Reassign_CompletedActivities"),
            },
            maybeCurrentStep: null,
        };
    }
    // All activities must have the same status (be in the same current step)
    const currentStep = selection[0].original.status;
    if (selection.some((row) => row.original.status !== currentStep)) {
        return {
            textToolState: {
                type: "DISABLED",
                reason: t("TestingActivities_Reassign_SameStatus"),
            },
            maybeCurrentStep: null,
        };
    }

    // Can not reassign activities with different number of steps
    const firstRow = selection[0].original;
    const hasFirstReview = firstRow.firstReviewInfo !== null;
    const hasSecondReview = firstRow.secondReviewInfo !== null;

    if (
        selection.some((row) => {
            const rowHasFirstReview = row.original.firstReviewInfo !== null;
            const rowHasSecondReview = row.original.secondReviewInfo !== null;
            return (
                rowHasFirstReview !== hasFirstReview ||
                rowHasSecondReview !== hasSecondReview
            );
        })
    ) {
        return {
            textToolState: {
                type: "DISABLED",
                reason: t("TestingActivities_Reassign_DifferentNumberOfSteps"),
            },
            maybeCurrentStep: null,
        };
    }

    return {
        textToolState: { type: "ENABLED" },
        maybeCurrentStep: currentStep,
    };
}
